import { fg } from '@atlaskit/platform-feature-flags';

import type { Maybe, SearchConfluenceSubtype, SearchResult } from '../../../../common/types';

const includeSocialSignalsDirective = () =>
	fg('rovo_disable_social_signal_on_qf') ? '@include(if: $includeSocialSignals) ' : '';

const SearchConfluencePageBlogAttachment = (serveUserShardUI: boolean) => `
... on SearchConfluencePageBlogAttachment {
	lastModified
	${
		serveUserShardUI
			? `
	latestUserAction @include(if: $includeUserSignals)
	latestUserActionTs @include(if: $includeUserSignals)
	`
			: ''
	}
	isVerified
	space {
		id
		name
		webUiLink
	}
	__typename
	confluenceEntity @include(if: $includeConfluenceEntity) {
		... on ConfluencePage {
			subtype
			${
				fg('search_page_limit_agg_hydration')
					? ''
					: `
			ancestors {
				id
				title
				links {
					webUi
				}
			}
			metadata {
				titleEmojiPublished {
					id
					value
				}
			}
			`
			}
			commentCountSummary {
				total
			}
			likesSummary {
				count
			}
		}
	}
	socialSignal(tenantId: $tenantId) ${includeSocialSignalsDirective()}{
		signal
		timestamp
		user {
			accountId
			name
			picture
			... on AtlassianAccountUser {
				email
				extendedProfile {
					jobTitle
				}
			}
		}
	}
	linkedResults {
		id
		title
		url
		type
		description
		lastModifiedDate
		category
		subtype
		integrationId
		providerId
	}
}
`;

export interface SearchConfluencePageBlogAttachment extends SearchResult {
	lastModified?: Maybe<string>;
	latestUserAction?: Maybe<string>;
	latestUserActionTs?: Maybe<string>;
	isVerified: boolean | null;
	confluenceEntity?: {
		subtype?: SearchConfluenceSubtype;
		ancestors?: {
			id: string;
			title: string;
			links?: {
				webUi: string;
			};
		}[];
		metadata?: Maybe<{
			titleEmojiPublished: Maybe<{
				id: string;
				value: string;
			}>;
		}>;
		commentCountSummary: Maybe<{
			total: Maybe<number>;
		}>;
		likesSummary: Maybe<{
			count: Maybe<number>;
		}>;
	};
	space?: Maybe<{
		id: string;
		name: string;
		webUiLink?: string;
	}>;
	socialSignal: Maybe<{
		/** Should be either "comment" , "edit", or "share". */
		signal: Maybe<string>;
		timestamp: Maybe<string>;
		user: Maybe<{
			accountId: string;
			name: string;
			picture: string;
			email: Maybe<string>;
			extendedProfile: Maybe<{
				jobTitle: Maybe<string>;
			}>;
		}>;
	}>;
	__typename: 'SearchConfluencePageBlogAttachment';
}

export interface ISearchConfluencePageBlogAttachment {
	readonly __typename: string;
}

export const isSearchConfluencePageBlogAttachment = (
	partial: ISearchConfluencePageBlogAttachment,
): partial is SearchConfluencePageBlogAttachment => {
	return partial.__typename === 'SearchConfluencePageBlogAttachment';
};

const SearchConfluenceSpace = `
... on SearchConfluenceSpace {
	key
	lastModified
	iconPath
	socialSignal(tenantId: $tenantId) ${includeSocialSignalsDirective()}{
		signal
		timestamp
		user {
			accountId
			name
			picture
			... on AtlassianAccountUser {
				email
				extendedProfile {
					jobTitle
				}
			}
		}

	}
}
`;

export interface SearchConfluenceSpace extends SearchResult {
	key?: string;
	lastModified?: Maybe<string>;
	iconPath?: Maybe<string>;
	latestUserAction?: Maybe<string>;
	latestUserActionTs?: Maybe<string>;
	socialSignal: Maybe<{
		/** Should be either "comment" , "edit", or "share". */
		signal: Maybe<string>;
		timestamp: Maybe<string>;
		user: Maybe<{
			accountId: string;
			name: string;
			picture: string;
			email: Maybe<string>;
			extendedProfile: Maybe<{
				jobTitle: Maybe<string>;
			}>;
		}>;
	}>;
	__typename: 'SearchConfluenceSpace';
}

export interface ISearchConfluenceSpace {
	readonly __typename: string;
}

export const isSearchConfluenceSpace = (
	partial: ISearchConfluenceSpace,
): partial is SearchConfluenceSpace => {
	return partial.__typename === 'SearchConfluenceSpace';
};

export type SearchConfluencePartial = SearchConfluencePageBlogAttachment | SearchConfluenceSpace;

export const isSearchConfluencePartial = (
	partial: ISearchConfluenceSpace,
): partial is SearchConfluencePartial => {
	return isSearchConfluencePageBlogAttachment(partial) || isSearchConfluenceSpace(partial);
};

export const getConfluencePartials = (serveUserShardUI: boolean = false) => {
	return `
		${SearchConfluencePageBlogAttachment(serveUserShardUI)}
		${SearchConfluenceSpace}
	`;
};
